import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Search, Save, X,
  AlertCircle, Eye, Download, FileText,
  ArrowUpDown, ArrowUp, ArrowDown,
  Loader2, ChevronDown
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { API_BASE_URL } from '../../../api/config';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import Button from '../../../components/common/Button/Button';
import { formatDate } from '../../../utils/stringUtils';
import {
  useQuizzes,
  useQuestions,
  useCoursesForDropdown,
  useCreateQuestion,
  useUpdateQuestion,
  useDeleteQuestion,
  useCreateQuiz,
  useUpdateQuiz,
  useDeleteQuiz
} from '../../../hooks/useTraining';
import toast from 'react-hot-toast';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import { CheckSquare, Square, Check } from 'lucide-react';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import trainingService from '../../../api/services/trainingService';

const QUESTION_TYPES = [
  { value: 'mcq_single', label: 'Multiple Choice', icon: '○' }
];

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', color: 'text-green-600 bg-green-100' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-600 bg-yellow-100' },
  { value: 'hard', label: 'Hard', color: 'text-red-600 bg-red-100' }
];

const ALLOWED_QUESTION_TYPES = ['mcq_single'];

const capitalizeFirstLetter = (text) => {
  if (!text || typeof text !== 'string') return '';
  const match = text.match(/^(\s*)(.)/);
  if (!match) return text;
  const [, leading, first] = match;
  return leading + first.toUpperCase() + text.slice(leading.length + 1);
};

const truncateText = (text, maxLength = 40) => {
  if (!text || typeof text !== 'string') return '';
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
};

const getQuestionTypeLabel = (type) => {
  const matched = QUESTION_TYPES.find(t => t.value === type);
  if (matched) return matched.label;
  if (!type) return 'General Question';
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getQuestionTypeValue = (question) => {
  if (!question || typeof question !== 'object') return '';
  return (
    question.type ??
    question.questionType ??
    question.question_type ??
    question?.question?.type ??
    question?.metadata?.type ??
    ''
  );
};

const getQuestionDifficultyValue = (question) => {
  if (!question || typeof question !== 'object') return '';
  return (
    question.difficulty ??
    question.difficultyLevel ??
    question.difficulty_level ??
    question?.question?.difficulty ??
    question?.metadata?.difficulty ??
    ''
  );
};

const getQuestionPointsValue = (question) => {
  if (!question || typeof question !== 'object') return 0;
  const candidates = [
    question.points,
    question.point,
    question.pointValue,
    question.point_score,
    question.pointScore,
    question.weight,
    question.score,
    question.marks,
    question.totalPoints,
    question.mark
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') continue;
    const numeric = Number(candidate);
    if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return 0;
};

const getDifficultyDisplay = (value) => {
  const matched = DIFFICULTIES.find(d => d.value === value);
  if (matched) return matched;
  if (!value) {
    return { label: 'Not set', color: 'text-gray-600 bg-gray-100' };
  }
  return {
    label: capitalizeFirstLetter(String(value).replace(/[_-]/g, ' ')),
    color: 'text-gray-600 bg-gray-100'
  };
};

const formatDateTime = (value) => {
  return formatDate(value, { includeTime: true }) || 'Not available';
};

const QuizQuestionBank = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isFirstRender = useRef(true);

  const [activeTab, setActiveTab] = useState(searchParams.get('subtab') || 'quizzes');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [searchInputRef, setSearchInputRef] = useState(null);
  const [selectedTags] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(searchParams.get('difficulty') || '');
  const [filterStatus, setFilterStatus] = useState(''); // Added for export filtering compatibility
  const [quizPage, setQuizPage] = useState(Number(searchParams.get('quizPage')) || 1);
  const [quizItemsPerPage, setQuizItemsPerPage] = useState(Number(searchParams.get('quizLimit')) || 10);
  const [questionPage, setQuestionPage] = useState(Number(searchParams.get('questionPage')) || 1);
  const [questionItemsPerPage, setQuestionItemsPerPage] = useState(Number(searchParams.get('questionLimit')) || 10);
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || '');
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || '');

  // Question Bank State
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, questionId: null, type: null });

  // Quiz State
  const [showAddQuiz, setShowAddQuiz] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    passScore: 70,
    showSolutions: 'on_fail',
    questions: []
  });

  // Question selection state
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const [debouncedQuestionSearchTerm, setDebouncedQuestionSearchTerm] = useState('');
  const [questionSubjectFilter, setQuestionSubjectFilter] = useState('');
  const [questionDifficultyFilter, setQuestionDifficultyFilter] = useState('');
  const [questionTypeFilter, setQuestionTypeFilter] = useState('');

  // Export and Selection State
  const [selectedItemsMap, setSelectedItemsMap] = useState(new Map());
  const [showExportModal, setShowExportModal] = useState(false);

  // Bulk Delete State
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Reset selection when tab changes
  useEffect(() => {
    setSelectedItemsMap(new Map());
  }, [activeTab, debouncedSearchTerm, debouncedQuestionSearchTerm]);

  // Reset filters when changing tabs (excluding the initial render)
  useEffect(() => {
    if (isFirstRender.current) return;
    setSearchTerm('');
    setSelectedDifficulty('');
    setQuestionSubjectFilter('');
    setFilterStartDate('');
    setFilterEndDate('');
    setQuizPage(1);
    setQuestionPage(1);
  }, [activeTab]);

  // Helper to check selection status
  const isSelected = (id) => selectedItemsMap.has(id);
  const selectedCount = selectedItemsMap.size;

  // Sort State
  const [quizSortField, setQuizSortField] = useState(null);
  const [questionSortField, setQuestionSortField] = useState(null);
  const [quizSortOrder, setQuizSortOrder] = useState('asc');
  const [questionSortOrder, setQuestionSortOrder] = useState('asc');

  // React Query Hooks for Data Fetching
  const {
    data: quizzesResponse,
    isLoading: quizzesLoading,
    error: quizzesError,
    refetch: refetchQuizzes
  } = useQuizzes({
    search: debouncedSearchTerm || '',
    page: quizPage,
    limit: quizItemsPerPage,
    startDate: filterStartDate,
    endDate: filterEndDate
  });

  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);

  const baseExportFilters = useMemo(() => ({
    activeTab,
    q: searchTerm,
    subject: questionSubjectFilter,
    difficulty: activeTab === 'quizzes' ? '' : selectedDifficulty,
    type: activeTab === 'quizzes' ? '' : questionTypeFilter,
    startDate: filterStartDate,
    endDate: filterEndDate,
  }), [activeTab, searchTerm, selectedDifficulty, questionSubjectFilter, questionTypeFilter, filterStartDate, filterEndDate]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: "training-quiz-bank",
        scope,
        format,
        filters,
        page: overridePage ?? (activeTab === 'quizzes' ? quizPage : questionPage),
        limit: overrideLimit ?? (activeTab === 'quizzes' ? quizItemsPerPage : questionItemsPerPage),
        sortBy: "createdAt",
        sortOrder: "desc",
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Export failed");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `training-quiz-bank-${activeTab.toLowerCase()}-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, activeTab, quizPage, questionPage, quizItemsPerPage, questionItemsPerPage]
  );

  const {
    data: questionsResponse,
    isLoading: questionsLoading,
    error: questionsError,
    refetch: refetchQuestions
  } = useQuestions({
    search: activeTab === 'questions' ? (debouncedSearchTerm || '') : (debouncedQuestionSearchTerm || ''),
    difficulty: activeTab === 'questions' ? (selectedDifficulty || '') : (questionDifficultyFilter || ''),
    subject: activeTab === 'questions' ? (questionSubjectFilter || '') : (questionSubjectFilter || ''),
    page: activeTab === 'questions' ? questionPage : 1,
    limit: activeTab === 'questions' ? questionItemsPerPage : 100,
    startDate: filterStartDate,
    endDate: filterEndDate
  });

  const {
    data: courseOptions = [],
    isLoading: coursesLoading,
    error: coursesError
  } = useCoursesForDropdown();

  // Mutation Hooks
  const createQuestionMutation = useCreateQuestion();
  const updateQuestionMutation = useUpdateQuestion();
  const deleteQuestionMutation = useDeleteQuestion();
  const createQuizMutation = useCreateQuiz();
  const updateQuizMutation = useUpdateQuiz();
  const deleteQuizMutation = useDeleteQuiz();

  const quizzes = quizzesResponse?.data?.quizzes || quizzesResponse?.data || [];
  const questions = questionsResponse?.data?.questions || questionsResponse?.data || [];
  const quizPagination = quizzesResponse?.pagination || quizzesResponse?.data?.pagination || {};
  const questionPagination = questionsResponse?.pagination || questionsResponse?.data?.pagination || {};

  const loading = quizzesLoading || questionsLoading || createQuestionMutation.isPending || updateQuestionMutation.isPending || deleteQuestionMutation.isPending || createQuizMutation.isPending || updateQuizMutation.isPending || deleteQuizMutation.isPending;
  const error = quizzesError?.message || questionsError?.message;
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Helper function for text input key press (alphanumeric, spaces, commas, question marks only)
  const handleTextKeyPress = (e) => {
    const char = e.key;
    if (!/^[a-zA-Z0-9\s,?]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
      e.preventDefault();
    }
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  // Validation functions - using same pattern as ModulesLessons
  const validateQuestionForm = (form) => {
    const errors = {};

    // Validate title
    if (!form.title || form.title.trim().length === 0) {
      errors.title = 'Question title is required';
    } else if (form.title.trim().length < 3) {
      errors.title = 'Question title must be at least 3 characters';
    } else if (form.title.trim().length > 200) {
      errors.title = 'Question title must be less than 200 characters';
    } else if (/^[a-z]/.test(form.title.trim())) {
      errors.title = 'First letter of the question title must be capital';
    }

    // Validate question text
    if (!form.question || form.question.trim().length === 0) {
      errors.question = 'Question text is required';
    } else if (form.question.trim().length < 10) {
      errors.question = 'Question text must be at least 10 characters';
    } else if (form.question.trim().length > 1000) {
      errors.question = 'Question text must be less than 1000 characters';
    }

    // Validate type
    const validTypes = ALLOWED_QUESTION_TYPES;
    if (!form.type || !validTypes.includes(form.type)) {
      errors.type = 'Please select a valid question type';
    }

    // Validate difficulty
    const validDifficulties = ['easy', 'medium', 'hard'];
    if (!form.difficulty || !validDifficulties.includes(form.difficulty)) {
      errors.difficulty = 'Please select a valid difficulty level';
    }

    // Validate course selection
    if (!form.subject || form.subject.trim().length === 0) {
      errors.subject = 'Course is required';
    } else if (form.subject.trim().length > 100) {
      errors.subject = 'Course name must be less than 100 characters';
    }

    // Validate points - required field
    if (form.points === '' || form.points === null || form.points === undefined) {
      errors.points = 'Points are required';
    } else if (isNaN(Number(form.points)) || Number(form.points) < 1) {
      errors.points = 'Points must be at least 1';
    } else if (Number(form.points) > 100) {
      errors.points = 'Points must be less than 100';
    }

    // Validate options for MCQ questions
    if (form.type === 'mcq_single') {
      if (!form.options || !Array.isArray(form.options) || form.options.length < 2) {
        errors.options = 'At least 2 options are required for MCQ questions';
      } else if (form.options.length > 10) {
        errors.options = 'Maximum 10 options allowed for MCQ questions';
      } else {
        // Validate that each option has text
        const emptyOptions = form.options.filter(opt => !opt.text || opt.text.trim().length === 0);
        if (emptyOptions.length > 0) {
          errors.options = 'All options must have text';
        }

        // Validate that at least one option is marked as correct
        const correctOptions = form.options.filter(opt => opt.isCorrect);
        if (correctOptions.length === 0) {
          errors.options = 'At least one option must be marked as correct';
        }
      }
    }

    return errors;
  };

  const validateField = (name, value) => {
    let error = null;
    switch (name) {
      case 'title':
        if (!value || value.trim().length === 0) {
          error = 'Quiz title is required';
        } else if (value.trim().length < 3) {
          error = 'Quiz title must be at least 3 characters';
        } else if (value.trim().length > 100) {
          error = 'Quiz title must be less than 100 characters';
        }
        break;
      case 'description':
        if (!value || value.trim().length === 0) {
          error = 'Quiz description is required';
        } else if (value.trim().length < 10) {
          error = 'Quiz description must be at least 10 characters';
        } else if (value.trim().length > 500) {
          error = 'Quiz description must be less than 500 characters';
        }
        break;
      case 'passScore':
        if (value !== '' && value !== null && value !== undefined) {
          if (isNaN(Number(value)) || Number(value) < 0) {
            error = 'Pass score must be a non-negative number';
          } else if (Number(value) > 100) {
            error = 'Pass score must be less than or equal to 100';
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateQuizForm = (form) => {
    const errors = {};

    // Validate title
    const titleError = validateField('title', form.title);
    if (titleError) errors.title = titleError;

    // Validate description
    const descriptionError = validateField('description', form.description);
    if (descriptionError) errors.description = descriptionError;

    // Validate pass score
    const passScoreError = validateField('passScore', form.passScore);
    if (passScoreError) errors.passScore = passScoreError;

    return errors;
  };

  const [questionForm, setQuestionForm] = useState({
    title: '',
    type: 'mcq_single',
    difficulty: 'medium',
    subject: '',
    points: '',
    question: '',
    options: [],
    correctAnswer: '',
    explanation: ''
  });




  const getQuestionIdentifier = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string' || typeof entry === 'number') {
      return String(entry);
    }
    return (
      entry.id ||
      entry._id ||
      entry.questionId ||
      entry.question_id ||
      entry.question?.id ||
      entry.question?._id ||
      entry.value ||
      entry
    ) && String(
      entry.id ||
      entry._id ||
      entry.questionId ||
      entry.question_id ||
      entry.question?.id ||
      entry.question?._id ||
      entry.value ||
      entry
    );
  };

  const resolveQuizQuestions = useCallback((quizQuestions) => {
    if (!Array.isArray(quizQuestions)) return [];
    return quizQuestions.map((entry, index) => {
      const identifier = getQuestionIdentifier(entry);
      const matchedQuestion = questions.find(q => {
        const possibleIds = [
          q.id,
          q._id,
          q.questionId,
          q.question_id
        ]
          .filter(Boolean)
          .map(id => String(id));
        return identifier && possibleIds.includes(identifier);
      });

      if (matchedQuestion) {
        return { ...matchedQuestion };
      }

      if (entry && typeof entry === 'object') {
        return entry;
      }

      return {
        id: identifier || `quiz-question-${index}`,
        title: `Question ${index + 1}`,
        points: 0
      };
    });
  }, [questions]);

  // Quiz & question details view state
  const [viewingQuiz, setViewingQuiz] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);

  const mapQuizWithDetails = useCallback((quiz) => {
    if (!quiz) return null;
    const resolvedQuestions = resolveQuizQuestions(quiz.questions);
    const totalQuestions = resolvedQuestions.length || quiz.totalQuestions || 0;
    const totalPoints = resolvedQuestions.length > 0
      ? resolvedQuestions.reduce((sum, question) => sum + getQuestionPointsValue(question), 0)
      : (quiz.totalPoints || 0);

    return {
      ...quiz,
      resolvedQuestions,
      resolvedTotals: {
        totalQuestions,
        totalPoints
      }
    };
  }, [resolveQuizQuestions]);

  const viewingQuizQuestions = useMemo(() => {
    if (!viewingQuiz) return [];
    if (Array.isArray(viewingQuiz.resolvedQuestions)) {
      return viewingQuiz.resolvedQuestions;
    }
    return resolveQuizQuestions(viewingQuiz.questions);
  }, [resolveQuizQuestions, viewingQuiz]);

  const viewingQuizQuestionCount = viewingQuiz?.resolvedTotals?.totalQuestions
    ?? (viewingQuizQuestions.length || viewingQuiz?.totalQuestions || 0);

  const viewingQuizTotalPoints = viewingQuiz?.resolvedTotals?.totalPoints
    ?? (viewingQuizQuestions.length > 0
      ? viewingQuizQuestions.reduce((sum, question) => sum + getQuestionPointsValue(question), 0)
      : (viewingQuiz?.totalPoints || 0));

  const subjectOptions = useMemo(() => {
    const normalizedCourses = (courseOptions || [])
      .map((course) => {
        const id =
          course?._id ||
          course?.id ||
          course?.courseId ||
          course?.value ||
          course?.slug ||
          course?.code ||
          course?.title ||
          course?.name ||
          course?.courseTitle;
        const label = course?.title || course?.name || course?.courseTitle || course?.code || '';
        if (!label) return null;
        return { id: String(id || label), label, isLegacy: false };
      })
      .filter(Boolean);

    const optionMap = new Map();
    normalizedCourses.forEach((option) => {
      if (!optionMap.has(option.id)) {
        optionMap.set(option.id, option);
      }
    });

    const ensureLegacyOption = (value) => {
      if (!value) return;
      const exists = Array.from(optionMap.values()).some(opt => opt.label === value);
      if (!exists) {
        optionMap.set(`legacy-${value}`, { id: `legacy-${value}`, label: value, isLegacy: true });
      }
    };

    ensureLegacyOption(questionForm.subject);
    ensureLegacyOption(questionSubjectFilter);
    (questions || []).forEach(question => {
      if (typeof question?.subject === 'string') {
        ensureLegacyOption(question.subject);
      }
    });

    return Array.from(optionMap.values());
  }, [courseOptions, questionForm.subject, questionSubjectFilter, questions]);

  // Debounce search term to prevent API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isFirstRender.current) return;
    setQuizPage(1);
    setQuestionPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (isFirstRender.current) return;
    setQuestionPage(1);
    setQuizPage(1);
  }, [selectedDifficulty, questionSubjectFilter, filterStartDate, filterEndDate]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['subtab', 'search', 'difficulty', 'subject', 'quizPage', 'quizLimit', 'questionPage', 'questionLimit', 'startDate', 'endDate'];
      managedKeys.forEach(key => params.delete(key));

      if (activeTab && activeTab !== 'quizzes') params.set('subtab', activeTab);
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (selectedDifficulty) params.set('difficulty', selectedDifficulty);
      if (questionSubjectFilter) params.set('subject', questionSubjectFilter);
      if (quizPage && quizPage !== 1) params.set('quizPage', String(quizPage));
      if (quizItemsPerPage && quizItemsPerPage !== 10) params.set('quizLimit', String(quizItemsPerPage));
      if (questionPage && questionPage !== 1) params.set('questionPage', String(questionPage));
      if (questionItemsPerPage && questionItemsPerPage !== 10) params.set('questionLimit', String(questionItemsPerPage));
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);

      return params;
    }, { replace: true });

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [
    activeTab,
    debouncedSearchTerm,
    selectedDifficulty,
    questionSubjectFilter,
    quizPage,
    quizItemsPerPage,
    questionPage,
    questionItemsPerPage,
    filterStartDate,
    filterEndDate,
    setSearchParams
  ]);

  // Debounce question search term for quiz creation modal
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuestionSearchTerm(questionSearchTerm);
    }, 300); // 300ms delay for faster response in modal

    return () => clearTimeout(timer);
  }, [questionSearchTerm]);

  // Keep focus on search input only during typing (not after re-renders)
  useEffect(() => {
    // Only refocus if user was actively typing and lost focus due to re-render
    if (searchInputRef && searchTerm && document.activeElement !== searchInputRef) {
      const timer = setTimeout(() => {
        if (searchInputRef && searchTerm) {
          searchInputRef.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  });

  const questionTypes = QUESTION_TYPES;
  const difficulties = DIFFICULTIES;

  const showSolutionsOptions = [
    { value: 'never', label: 'Never' },
    { value: 'on_pass', label: 'On Pass' },
    { value: 'on_fail', label: 'On Fail' },
    { value: 'always', label: 'Always' }
  ];

  const handleAddQuestion = () => {
    setQuestionForm({
      title: '',
      type: 'mcq_single',
      difficulty: 'medium',
      subject: '',
      points: '',
      question: '',
      options: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ],
      correctAnswer: 'true',
      explanation: ''
    });
    setShowAddQuestion(true);
    setEditingQuestion(null);
    setValidationErrors({});
  };

  const handleEditQuestion = (question) => {

    setEditingQuestion(question);

    // Map backend data to frontend form structure - use actual values without fallbacks
    const rawType = question.type || question.questionType || 'mcq_single';
    const normalizedType = ALLOWED_QUESTION_TYPES.includes(rawType) ? rawType : 'mcq_single';
    const defaultOptions = [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ];
    let mappedOptions;
    if (normalizedType === 'mcq_single') {
      if (Array.isArray(question.options) && question.options.length >= 2) {
        mappedOptions = question.options;
      } else if ((question.type || question.questionType) === 'true_false') {
        const correctValue = String(question.correctAnswer || 'true').toLowerCase();
        mappedOptions = [
          { text: 'True', isCorrect: correctValue !== 'false' },
          { text: 'False', isCorrect: correctValue === 'false' }
        ];
      } else {
        mappedOptions = defaultOptions;
      }
    } else {
      mappedOptions = defaultOptions;
    }

    const mappedFormData = {
      title: question.title || question.question || '',
      type: normalizedType,
      difficulty: question.difficulty || '',
      subject: question.subject || '',
      points: getQuestionPointsValue(question) || '',
      question: question.question || '',
      options: mappedOptions,
      correctAnswer: 'true',
      explanation: question.explanation || ''
    };


    setQuestionForm(mappedFormData);
    setShowAddQuestion(true);
    setValidationErrors({});
  };

  const handleSaveQuestion = async () => {
    // Validate form before submission - same pattern as ModulesLessons
    const formErrors = validateQuestionForm(questionForm);

    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          errorElement.focus();
        } else {
          // Fallback: scroll to first error field by finding any field with validation error
          const errorFields = document.querySelectorAll('[data-field]');
          for (let field of errorFields) {
            const fieldName = field.getAttribute('data-field');
            if (formErrors[fieldName]) {
              field.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              field.focus();
              break;
            }
          }
        }
      }, 100);

      return;
    }


    setValidationErrors({});

    try {
      // Prepare question data - preserve empty values without fallbacks
      const questionData = {
        title: questionForm.title,
        question: questionForm.question,
        type: questionForm.type,
        difficulty: questionForm.difficulty,
        subject: questionForm.subject,
        points: Number(questionForm.points),
        options: questionForm.options || [],
        correctAnswer: questionForm.type === 'mcq_single'
          ? questionForm.options.filter(opt => opt.isCorrect).map(opt => opt.text).join(',')
          : questionForm.correctAnswer,
        explanation: questionForm.explanation
      };


      if (editingQuestion) {
        // Update existing question
        updateQuestionMutation.mutate({ id: editingQuestion.id, data: questionData }, {
          onSuccess: (response) => {
            if (response.success) {
              setEditingQuestion(null);
              setShowAddQuestion(false);
            }
          }
        });
      } else {
        // Create new question
        createQuestionMutation.mutate(questionData, {
          onSuccess: (response) => {
            if (response.success) {
              setShowAddQuestion(false);
            }
          }
        });
      }
    } catch (error) {
      toast.error('Failed to save question. Please try again.');
    }
  };

  const handleDeleteQuestion = (questionId) => {
    setDeleteConfirmation({ show: true, questionId, type: 'question' });
  };

  const handleConfirmDelete = async () => {
    const { questionId, type } = deleteConfirmation;

    setDeleteConfirmation({ show: false, questionId: null, type: null });

    try {
      if (type === 'question') {
        deleteQuestionMutation.mutate(questionId);
      } else if (type === 'quiz') {
        deleteQuizMutation.mutate(questionId);
      }
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ show: false, questionId: null, type: null });
  };

  const handleAddQuiz = () => {
    setQuizForm({
      title: '',
      description: '',
      passScore: 70,
      showSolutions: 'on_fail',
      questions: []
    });
    setSelectedQuestions([]);
    setQuestionSearchTerm('');
    setQuestionSubjectFilter('');
    setQuestionDifficultyFilter('');
    setQuestionTypeFilter('');
    setShowAddQuiz(true);
    setEditingQuiz(null);
    setValidationErrors({});
    setTouched({});
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuiz(quiz);

    // Map backend data to frontend form structure - use actual values without fallbacks
    const mappedQuizData = {
      title: quiz.title || '',
      description: quiz.description || '',
      passScore: quiz.passScore || '',
      showSolutions: quiz.showSolutions || '',
      questions: quiz.questions || []
    };

    setQuizForm(mappedQuizData);

    // If quiz has questions, find them in the existing questions array
    if (quiz.questions && quiz.questions.length > 0) {
      // Note: We might not have all questions loaded if we're using pagination
      // but the resolveQuizQuestions handles it by showing placeholders if not found
      const validQuestions = questions.filter(question =>
        quiz.questions.includes(question.id) ||
        quiz.questions.includes(question._id) ||
        quiz.questions.some(qId => String(qId) === String(question.id)) ||
        quiz.questions.some(qId => String(qId) === String(question._id))
      );
      setSelectedQuestions(validQuestions);
    } else {
      setSelectedQuestions([]);
    }

    setShowAddQuiz(true);
    setValidationErrors({});
    setTouched({});
  };

  // --- Selection Handlers ---

  const handleSelectRow = (item) => {
    const id = item.id || item._id;
    setSelectedItemsMap(prev => {
      const newMap = new Map(prev);
      if (newMap.has(id)) {
        newMap.delete(id);
      } else {
        newMap.set(id, item);
      }
      return newMap;
    });
  };

  const handleSelectAll = () => {
    const items = activeTab === 'quizzes' ? quizzes : questions;
    const allIds = items.map(item => item.id || item._id);
    const allPageSelected = allIds.every(id => selectedItemsMap.has(id));

    if (allPageSelected) {
      // User requested: "when user uncheck from table header... then it reset all the selected row"
      // So we clear the entire map.
      setSelectedItemsMap(new Map());
    } else {
      // Select all on current page
      setSelectedItemsMap(prev => {
        const newMap = new Map(prev);
        items.forEach(item => {
          newMap.set(item.id || item._id, item);
        });
        return newMap;
      });
    }
  };

  // --- Bulk Delete Handlers ---

  const handleBulkDelete = () => {
    if (selectedItemsMap.size === 0) {
      toast.error('Please select at least one item to delete');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    const ids = Array.from(selectedItemsMap.keys());
    let successCount = 0;
    let errorCount = 0;

    for (const id of ids) {
      try {
        const result = await (activeTab === 'questions'
          ? trainingService.deleteQuestion(id)
          : trainingService.deleteQuiz(id));
        if (result && result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        errorCount++;
      }
    }

    setIsBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedItemsMap(new Map());

    // Invalidate queries so tables refresh
    if (activeTab === 'questions') {
      refetchQuestions();
    } else {
      refetchQuizzes();
    }

    if (successCount > 0) toast.success(`${successCount} ${activeTab === 'questions' ? 'question(s)' : 'quiz(zes)'} deleted successfully`);
    if (errorCount > 0) toast.error(`Failed to delete ${errorCount} item(s)`);
  };

  // --- Export Handlers ---

  const handleExportClick = () => {
    if (selectedCount > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedItemsMap.keys());
      if (!ids.length) {
        toast.error(`No selected ${activeTab.toLowerCase()} found to export.`);
        return;
      }
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} ${activeTab.toLowerCase()} exported!`, { id: toastId });
        setShowInlineSelectedFormats(false);
      } catch (error) {
          toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [selectedItemsMap, activeTab, requestBackendExport]
  );

  const handleExportScoped = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        const currentData = activeTab === 'quizzes' ? quizzes : questions;
        if (scope === 'currentPage' && (!currentData || !currentData.length)) {
          toast.error(`No ${activeTab.toLowerCase()} on current page to export.`, { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, createdAtStart: startDate, createdAtEnd: endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: { activeTab },
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {
          toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [quizzes, questions, activeTab, baseExportFilters, requestBackendExport]
  );

  const handleViewQuizDetails = (quiz) => {
    setViewingQuiz(mapQuizWithDetails(quiz));
  };

  const handleCloseQuizDetails = () => {
    setViewingQuiz(null);
  };

  const handleViewQuestionDetails = (question) => {
    setViewingQuestion(question);
  };

  const handleCloseQuestionDetails = () => {
    setViewingQuestion(null);
  };

  const handleSaveQuiz = async () => {
    // Validate form before submission - same pattern as ModulesLessons
    const formErrors = validateQuizForm(quizForm);

    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      // Mark all fields as touched
      setTouched({
        title: true,
        description: true,
        passScore: true
      });

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          errorElement.focus();
        } else {
          // Fallback: scroll to first error field by finding any field with validation error
          const errorFields = document.querySelectorAll('[data-field]');
          for (let field of errorFields) {
            const fieldName = field.getAttribute('data-field');
            if (formErrors[fieldName]) {
              field.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              field.focus();
              break;
            }
          }
        }
      }, 100);

      return;
    }

    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question for the quiz');
      return;
    }

    try {
      const quizData = {
        ...quizForm,
        questions: selectedQuestions.filter(q => q && (q.id || q._id)).map(q => q.id || q._id),
        questionCount: selectedQuestions.length,
        totalPoints: selectedQuestions
          .filter(q => q && (q.id || q._id))
          .reduce((sum, q) => sum + getQuestionPointsValue(q), 0)
      };

      if (editingQuiz) {
        updateQuizMutation.mutate({ id: editingQuiz.id, data: quizData }, {
          onSuccess: (response) => {
            if (response.success) {
              setEditingQuiz(null);
              setShowAddQuiz(false);
            }
          }
        });
      } else {
        createQuizMutation.mutate(quizData, {
          onSuccess: (response) => {
            if (response.success) {
              setShowAddQuiz(false);
            }
          }
        });
      }
    } catch (error) {
      toast.error('Failed to save quiz. Please try again.');
    }
  };

  const handleDeleteQuiz = (quizId) => {
    setDeleteConfirmation({ show: true, questionId: quizId, type: 'quiz' });
  };

  // Question selection handlers
  const handleSelectQuestion = (question) => {
    const isSelected = selectedQuestions.some(q => q && q.id === question.id);
    if (isSelected) {
      setSelectedQuestions(prev => prev.filter(q => q && q.id !== question.id));
    } else {
      setSelectedQuestions(prev => [...prev, question]);
    }
  };

  const handleRemoveQuestion = (questionId) => {
    setSelectedQuestions(prev => prev.filter(q => q && q.id !== questionId));
  };

  const handleSelectAllQuestions = () => {
    const filteredQuestions = getFilteredQuestionsForSelection();
    setSelectedQuestions(filteredQuestions);
  };

  const handleClearSelection = () => {
    setSelectedQuestions([]);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStartDate('');
    setFilterEndDate('');
    setSelectedDifficulty('');
    setQuestionSubjectFilter('');
    setQuizPage(1);
    setQuestionPage(1);
  };

  const handleQuizPageChange = (page) => {
    if (!page || page === quizPage || page < 1) return;
    const totalPages = quizPagination?.totalPages;
    if (totalPages && page > totalPages) return;
    setQuizPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuizItemsPerPageChange = (limit) => {
    if (!limit || limit === quizItemsPerPage) return;
    setQuizItemsPerPage(limit);
    setQuizPage(1);
  };

  const handleQuestionPageChange = (page) => {
    if (!page || page === questionPage || page < 1) return;
    const totalPages = questionPagination?.totalPages;
    if (totalPages && page > totalPages) return;
    setQuestionPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuestionItemsPerPageChange = (limit) => {
    if (!limit || limit === questionItemsPerPage) return;
    setQuestionItemsPerPage(limit);
    setQuestionPage(1);
  };

  const getFilteredQuestionsForSelection = () => {
    const subjectFilter = (questionSubjectFilter || '').toLowerCase();
    return (questions || []).filter(question => {
      const query = debouncedQuestionSearchTerm.toLowerCase();
      const questionTitle = question.title?.toLowerCase() || '';
      const questionText = question.question?.toLowerCase() || '';
      const subjectValue = (question.subject || '').toLowerCase();
      const matchesSearch =
        questionTitle.includes(query) ||
        questionText.includes(query) ||
        subjectValue.includes(query);
      const typeValue = getQuestionTypeValue(question);
      const difficultyValue = getQuestionDifficultyValue(question);
      const matchesSubject = !subjectFilter || subjectValue === subjectFilter;
      const matchesDifficulty = !questionDifficultyFilter || difficultyValue === questionDifficultyFilter;
      const matchesType = !questionTypeFilter || typeValue === questionTypeFilter;

      return matchesSearch && matchesSubject && matchesDifficulty && matchesType;
    });
  };

  const filteredQuestions = questions.filter(question => {
    const query = debouncedSearchTerm.toLowerCase();
    const questionTitle = question.title?.toLowerCase() || '';
    const questionText = question.question?.toLowerCase() || '';
    const subjectValue = (question.subject || '').toLowerCase();
    const matchesSearch =
      questionTitle.includes(query) ||
      questionText.includes(query) ||
      subjectValue.includes(query);
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => (question.tags || []).includes(tag));
    const difficultyValue = getQuestionDifficultyValue(question);
    const matchesDifficulty = !selectedDifficulty || difficultyValue === selectedDifficulty;

    const matchesDate = (!filterStartDate || !question.createdAt || new Date(question.createdAt) >= new Date(new Date(filterStartDate).setHours(0, 0, 0, 0))) &&
      (!filterEndDate || !question.createdAt || new Date(question.createdAt) <= new Date(new Date(filterEndDate).setHours(23, 59, 59, 999)));

    return matchesSearch && matchesTags && matchesDifficulty && matchesDate;
  });

  const filteredQuizzes = quizzes
    .map(mapQuizWithDetails)
    .filter(quiz => {
      const matchesSearch = quiz?.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        quiz?.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      const matchesDate = (!filterStartDate || !quiz.createdAt || new Date(quiz.createdAt) >= new Date(new Date(filterStartDate).setHours(0, 0, 0, 0))) &&
        (!filterEndDate || !quiz.createdAt || new Date(quiz.createdAt) <= new Date(new Date(filterEndDate).setHours(23, 59, 59, 999)));

      return matchesSearch && matchesDate;
    });

  const handleQuizSort = (field) => {
    if (quizSortField === field) {
      setQuizSortOrder(quizSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setQuizSortField(field);
      setQuizSortOrder('asc');
    }
  };

  const handleQuestionSort = (field) => {
    if (questionSortField === field) {
      setQuestionSortOrder(questionSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setQuestionSortField(field);
      setQuestionSortOrder('asc');
    }
  };

  const sortedQuizzes = useMemo(() => {
    if (!filteredQuizzes) return [];
    if (!quizSortField) return filteredQuizzes;

    return [...filteredQuizzes].sort((a, b) => {
      let aValue, bValue;

      switch (quizSortField) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'questions':
          aValue = a.resolvedTotals?.totalQuestions ?? a.totalQuestions ?? (a.questions?.length || 0);
          bValue = b.resolvedTotals?.totalQuestions ?? b.totalQuestions ?? (b.questions?.length || 0);
          break;
        case 'points':
          aValue = a.resolvedTotals?.totalPoints ?? (Array.isArray(a.questions) ? a.questions.reduce((sum, q) => sum + getQuestionPointsValue(q), 0) : a.totalPoints || 0);
          bValue = b.resolvedTotals?.totalPoints ?? (Array.isArray(b.questions) ? b.questions.reduce((sum, q) => sum + getQuestionPointsValue(q), 0) : b.totalPoints || 0);
          break;
        case 'passScore':
          aValue = Number(a.passScore || 0);
          bValue = Number(b.passScore || 0);
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return quizSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return quizSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredQuizzes, quizSortField, quizSortOrder]);

  const sortedQuestions = useMemo(() => {
    if (!filteredQuestions) return [];
    if (!questionSortField) return filteredQuestions;

    return [...filteredQuestions].sort((a, b) => {
      let aValue, bValue;

      switch (questionSortField) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'question':
          aValue = (a.question || '').toLowerCase();
          bValue = (b.question || '').toLowerCase();
          break;
        case 'difficulty':
          const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
          aValue = difficultyOrder[getQuestionDifficultyValue(a)] || 0;
          bValue = difficultyOrder[getQuestionDifficultyValue(b)] || 0;
          break;
        case 'points':
          aValue = getQuestionPointsValue(a);
          bValue = getQuestionPointsValue(b);
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return questionSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return questionSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredQuestions, questionSortField, questionSortOrder]);

  const quizTotalResults = quizPagination?.totalQuizzes ?? quizPagination?.total ?? quizPagination?.totalItems ?? quizzes.length;
  const questionTotalResults = questionPagination?.totalQuestions ?? questionPagination?.total ?? questionPagination?.totalItems ?? questions.length;
  const quizPageLimit = quizPagination?.limit || quizItemsPerPage || 10;
  const questionPageLimit = questionPagination?.limit || questionItemsPerPage || 10;
  const currentQuizPage = quizPagination?.currentPage || quizPage;
  const currentQuestionPage = questionPagination?.currentPage || questionPage;
  const quizRangeStart = quizTotalResults === 0 ? 0 : ((currentQuizPage - 1) * quizPageLimit) + 1;
  const quizRangeEnd = Math.min(currentQuizPage * quizPageLimit, quizTotalResults);
  const questionRangeStart = questionTotalResults === 0 ? 0 : ((currentQuestionPage - 1) * questionPageLimit) + 1;
  const questionRangeEnd = Math.min(currentQuestionPage * questionPageLimit, questionTotalResults);
  const quizTotalPages = quizPagination?.totalPages || Math.max(1, Math.ceil((quizTotalResults || 0) / (quizPageLimit || 1)));
  const questionTotalPages = questionPagination?.totalPages || Math.max(1, Math.ceil((questionTotalResults || 0) / (questionPageLimit || 1)));
  const shouldShowQuizPagination = !loading && (quizPagination?.totalPages > 1 || quizTotalResults > quizPageLimit);
  const shouldShowQuestionPagination = !loading && (questionPagination?.totalPages > 1 || questionTotalResults > questionPageLimit);

  // Export functionality


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  // Show error message if there's an error
  if (error && !loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <PermissionGate permission="training" action="read">
            <button
              onClick={() => {
                if (activeTab === 'quizzes') {
                  refetchQuizzes();
                } else {
                  refetchQuestions();
                }
              }}
              className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
            >
              Try Again
            </button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quiz & Question Bank</h1>
          <p className="text-gray-600 mt-1">Manage questions and create assessments</p>
        </div>

        <div className="flex items-center space-x-4">
          {selectedCount > 0 && (
            <PermissionGate permission="training" action="delete">
              <button
                onClick={handleBulkDelete}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedCount})
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="training" action="read">
            <div className="relative">
              <Button
                variant="outline"
                icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                Export {selectedCount > 0 ? `(${selectedCount})` : ""}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? "rotate-180" : ""}`} />
              </Button>

              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                onClose={() => setShowInlineSelectedFormats(false)}
                onExport={handleExportSelected}
                loading={exportLoading}
                className="absolute right-0 mt-2 z-50 min-w-[200px]"
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="training" action="create">
            <button
              onClick={handleAddQuestion}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Question
            </button>
          </PermissionGate>
          <PermissionGate permission="training" action="create">
            <button
              onClick={handleAddQuiz}
              className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Quiz
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <PermissionGate permission="training" action="read">
            <button
              onClick={() => setActiveTab('quizzes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'quizzes'
                ? 'border-agrosaf-green text-agrosaf-green'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Quizzes ({quizTotalResults || 0})
            </button>
          </PermissionGate>
          <PermissionGate permission="training" action="read">
            <button
              onClick={() => setActiveTab('questions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'questions'
                ? 'border-agrosaf-green text-agrosaf-green'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Questions ({questionTotalResults || 0})
            </button>
          </PermissionGate>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4 w-full">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={setSearchInputRef}
              type="text"
              placeholder={activeTab === 'quizzes' ? 'Search by Title...' : 'Search by Title...'}
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and common search/document symbols
                const value = e.target.value.replace(/[^a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and common search/document symbols
                if (!/^[a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              autoComplete="off"
              key="main-search"
              onFocus={(e) => {
                // Ensure focus is maintained
                e.target.focus();
              }}
              onBlur={(e) => {
                // Allow normal focus loss when clicking elsewhere
                // Only prevent focus loss if it's due to component re-render while typing
                if (searchTerm) {
                  setTimeout(() => {
                    // Check if focus was lost due to re-render (not user clicking elsewhere)
                    if (searchInputRef && !document.activeElement) {
                      searchInputRef.focus();
                    }
                  }, 50);
                }
              }}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setTimeout(() => searchInputRef?.focus(), 50);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex-shrink-0 min-w-[300px]">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={({ startDate, endDate }) => {
                setFilterStartDate(startDate);
                setFilterEndDate(endDate);
                setQuizPage(1);
                setQuestionPage(1);
              }}
            />
          </div>

          {activeTab === 'questions' && (
            <div className="flex-shrink-0">
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="px-3 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green text-sm"
              >
                <option value="">All Difficulties</option>
                {difficulties.map(diff => (
                  <option key={diff.value} value={diff.value}>{diff.label}</option>
                ))}
              </select>
            </div>
          )}

          {(searchTerm || filterStartDate || filterEndDate || selectedDifficulty || questionSubjectFilter) && (
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
                className="h-10"
              >
                Clear All
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {
        activeTab === 'quizzes' ? (
          <div className="space-y-4">
            {!loading && filteredQuizzes.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="flex flex-col items-center justify-center text-center">
                  <FileText className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No quizzes found</h3>
                  <p className="text-gray-500">
                    {(searchTerm || selectedDifficulty)
                      ? "Try adjusting your search or filter criteria."
                      : "Get started by creating your first quiz."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left w-10">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                onChange={handleSelectAll}
                                checked={quizzes.length > 0 && quizzes.every(q => isSelected(q.id || q._id))}
                              />
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuizSort('title')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Title</span>
                              {quizSortField === 'title' ? (
                                quizSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>

                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuizSort('questions')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Questions</span>
                              {quizSortField === 'questions' ? (
                                quizSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuizSort('points')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Points</span>
                              {quizSortField === 'points' ? (
                                quizSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuizSort('passScore')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Pass Score</span>
                              {quizSortField === 'passScore' ? (
                                quizSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuizSort('createdAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Created</span>
                              {quizSortField === 'createdAt' ? (
                                quizSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {sortedQuizzes.map(quiz => (
                          <tr key={quiz.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                  checked={isSelected(quiz.id || quiz._id)}
                                  onChange={() => handleSelectRow(quiz)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-semibold text-gray-900 capitalize max-w-xs truncate" title={quiz.title}>{quiz.title}</div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {quiz.resolvedTotals?.totalQuestions ?? quiz.totalQuestions ?? (quiz.questions?.length || 0)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {quiz.resolvedTotals?.totalPoints
                                  ?? (Array.isArray(quiz.questions)
                                    ? quiz.questions.reduce((sum, q) => sum + getQuestionPointsValue(q), 0)
                                    : quiz.totalPoints || 0)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{quiz.passScore}%</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-500">{formatDateTime(quiz.createdAt)}</div>
                            </td>
                            <td className="px-6 py-4 text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <PermissionGate permission="training" action="read">
                                  <button
                                    onClick={() => handleViewQuizDetails(quiz)}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="View Quiz Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="training" action="update">
                                  <button
                                    onClick={() => handleEditQuiz(quiz)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit Quiz"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="training" action="delete">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteQuiz(quiz.id);
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete Quiz"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {shouldShowQuizPagination && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {quizRangeStart} to {quizRangeEnd} of {quizTotalResults || 0} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={quizItemsPerPage}
                            onChange={(e) => handleQuizItemsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="training" action="read">
                          <button
                            onClick={() => handleQuizPageChange(currentQuizPage - 1)}
                            disabled={quizPagination?.hasPrevPage === false || currentQuizPage <= 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Previous
                          </button>
                        </PermissionGate>
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const pages = [];
                            if (quizTotalPages <= 10) {
                              for (let i = 1; i <= quizTotalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);
                              if (currentQuizPage <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(quizTotalPages);
                              } else if (currentQuizPage >= quizTotalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = quizTotalPages - 4; i <= quizTotalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentQuizPage - 1; i <= currentQuizPage + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(quizTotalPages);
                              }
                            }
                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`quiz-ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }
                              return (
                                <PermissionGate permission="training" action="read" key={`quiz-page-${page}`}>
                                  <button
                                    onClick={() => handleQuizPageChange(page)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentQuizPage
                                      ? 'bg-agrosaf-green text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                      }`}
                                  >
                                    {page}
                                  </button>
                                </PermissionGate>
                              );
                            });
                          })()}
                        </div>
                        <PermissionGate permission="training" action="read">
                          <button
                            onClick={() => handleQuizPageChange(currentQuizPage + 1)}
                            disabled={quizPagination?.hasNextPage === false || currentQuizPage >= quizTotalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {!loading && filteredQuestions.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="flex flex-col items-center justify-center text-center">
                  <FileText className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
                  <p className="text-gray-500">
                    {(searchTerm || selectedDifficulty)
                      ? "Try adjusting your search or filter criteria."
                      : "Get started by creating your first question."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left w-10">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                onChange={handleSelectAll}
                                checked={questions.length > 0 && questions.every(q => isSelected(q.id || q._id))}
                              />
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuestionSort('title')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Title</span>
                              {questionSortField === 'title' ? (
                                questionSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[35%] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuestionSort('question')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Question</span>
                              {questionSortField === 'question' ? (
                                questionSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuestionSort('difficulty')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Difficulty</span>
                              {questionSortField === 'difficulty' ? (
                                questionSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuestionSort('points')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Points</span>
                              {questionSortField === 'points' ? (
                                questionSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleQuestionSort('createdAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Created</span>
                              {questionSortField === 'createdAt' ? (
                                questionSortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                              ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                            </div>
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {sortedQuestions.map(question => {
                          const typeValue = getQuestionTypeValue(question);
                          const difficultyValue = getQuestionDifficultyValue(question);
                          const difficultyMeta = getDifficultyDisplay(difficultyValue);
                          const pointsValue = getQuestionPointsValue(question);
                          return (
                            <tr key={question.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                    checked={isSelected(question.id || question._id)}
                                    onChange={() => handleSelectRow(question)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm font-semibold max-w-xs truncate text-gray-900 capitalize">{question.title}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-700 max-w-xs truncate capitalize">{question.question}</div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${difficultyMeta.color}`}>
                                  {difficultyMeta.label}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                  {pointsValue} pts
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-500">{formatDateTime(question.createdAt)}</div>
                              </td>
                              <td className="px-6 py-4 text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-2">
                                  <PermissionGate permission="training" action="read">
                                    <button
                                      onClick={() => handleViewQuestionDetails(question)}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="View Question Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                  <PermissionGate permission="training" action="update">
                                    <button
                                      onClick={() => handleEditQuestion(question)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Edit Question"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                  <PermissionGate permission="training" action="delete">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteQuestion(question.id);
                                      }}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete Question"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </PermissionGate>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                {shouldShowQuestionPagination && (
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {questionRangeStart} to {questionRangeEnd} of {questionTotalResults || 0} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={questionItemsPerPage}
                            onChange={(e) => handleQuestionItemsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="training" action="read">
                          <button
                            onClick={() => handleQuestionPageChange(currentQuestionPage - 1)}
                            disabled={questionPagination?.hasPrevPage === false || currentQuestionPage <= 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Previous
                          </button>
                        </PermissionGate>
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const pages = [];
                            if (questionTotalPages <= 10) {
                              for (let i = 1; i <= questionTotalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);
                              if (currentQuestionPage <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(questionTotalPages);
                              } else if (currentQuestionPage >= questionTotalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = questionTotalPages - 4; i <= questionTotalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentQuestionPage - 1; i <= currentQuestionPage + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(questionTotalPages);
                              }
                            }
                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`question-ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }
                              return (
                                <PermissionGate permission="training" action="read" key={`question-page-${page}`}>
                                  <button
                                    onClick={() => handleQuestionPageChange(page)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentQuestionPage
                                      ? 'bg-agrosaf-green text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                      }`}
                                  >
                                    {page}
                                  </button>
                                </PermissionGate>
                              );
                            });
                          })()}
                        </div>
                        <PermissionGate permission="training" action="read">
                          <button
                            onClick={() => handleQuestionPageChange(currentQuestionPage + 1)}
                            disabled={questionPagination?.hasNextPage === false || currentQuestionPage >= questionTotalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      }

      {/* Add/Edit Question Modal */}
      {
        showAddQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingQuestion ? 'Edit Question' : 'Add New Question'}
                </h2>
              </div>


              <div className="p-6">
                {/* Error Display */}
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Question Title <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="title"
                          data-field="title"
                          value={questionForm.title}
                          maxLength={100}
                          required
                          onKeyPress={handleTextKeyPress}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Filter out invalid characters (alphanumeric, spaces, commas, question marks only)
                            value = value.replace(/[^a-zA-Z0-9\s,?]/g, '');
                            // Enforce max length
                            if (value.length > 100) {
                              value = value.substring(0, 100);
                            }
                            // Capitalize first letter
                            value = capitalizeFirstLetter(value);
                            setQuestionForm(prev => ({ ...prev, title: value }));
                            // Clear validation error when user starts typing
                            if (validationErrors.title) {
                              setValidationErrors(prev => ({ ...prev, title: null }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          placeholder="Enter question title"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {questionForm.title?.length || 0} / 100 characters
                        </p>
                        {validationErrors.title && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.title}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Course <span className="text-red-500">*</span></label>
                        <select
                          name="subject"
                          data-field="subject"
                          value={questionForm.subject}
                          onChange={(e) => {
                            setQuestionForm(prev => ({ ...prev, subject: e.target.value }));
                            // Clear validation error when user starts typing
                            if (validationErrors.subject) {
                              setValidationErrors(prev => ({ ...prev, subject: null }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.subject ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          disabled={subjectOptions.length === 0 && !questionForm.subject}
                        >
                          <option value="">Select Course</option>
                          {subjectOptions.length === 0 ? (
                            <option value="" disabled>
                              {coursesLoading ? 'Loading published courses...' : 'No published courses available'}
                            </option>
                          ) : (
                            subjectOptions.map(option => {
                              const optionLabel = `${option.label}${option.isLegacy ? ' (Legacy)' : ''}`;
                              return (
                                <option
                                  key={option.id}
                                  value={option.label}
                                  title={optionLabel}
                                >
                                  {truncateText(optionLabel, 40)}
                                </option>
                              );
                            })
                          )}
                        </select>
                        {coursesError && (
                          <p className="mt-1 text-sm text-yellow-600">
                            {coursesError}
                          </p>
                        )}
                        {validationErrors.subject && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.subject}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Question Type <span className="text-red-500">*</span></label>
                        <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
                          Multiple Choice (single correct answer)
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty <span className="text-red-500">*</span></label>
                        <select
                          value={questionForm.difficulty}
                          onChange={(e) => setQuestionForm(prev => ({ ...prev, difficulty: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        >
                          {difficulties.map(diff => (
                            <option key={diff.value} value={diff.value}>{diff.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Points <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          value={questionForm.points}
                          required
                          onKeyDown={handleNumberKeyDown}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^0-9]/g, '');
                            if (value && Number(value) > 100) {
                              value = '100';
                            }
                            setQuestionForm(prev => ({ ...prev, points: value }));
                            if (validationErrors.points) {
                              setValidationErrors(prev => ({ ...prev, points: null }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${validationErrors.points ? 'border-red-500 bg-red-50' : 'border-gray-300'
                            }`}
                          min="0"
                          max="100"
                          step="1"
                          placeholder="Enter points (1-100)"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Value: {questionForm.points || 0} (Max: 100)
                        </p>
                        {validationErrors.points && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.points}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Question Content</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Question Text <span className="text-red-500">*</span></label>
                      <textarea
                        name="question"
                        data-field="question"
                        value={questionForm.question}
                        maxLength={1000}
                        required
                        onKeyPress={handleTextKeyPress}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Filter out invalid characters (alphanumeric, spaces, commas, question marks only)
                          value = value.replace(/[^a-zA-Z0-9\s,?]/g, '');
                          // Enforce max length
                          if (value.length > 1000) {
                            value = value.substring(0, 1000);
                          }
                          setQuestionForm(prev => ({ ...prev, question: value }));
                          // Clear validation error when user starts typing
                          if (validationErrors.question) {
                            setValidationErrors(prev => ({ ...prev, question: null }));
                          }
                        }}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent resize-none ${validationErrors.question ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="Enter your question here..."
                      />
                      <div className="flex items-center justify-between mt-1">
                        {validationErrors.question && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.question}
                          </p>
                        )}
                        <span className={`text-xs ml-auto ${questionForm.question.length > 900 ? 'text-red-600' :
                          questionForm.question.length > 800 ? 'text-yellow-600' :
                            'text-gray-500'
                          }`}>
                          {questionForm.question.length}/1000
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Options for MCQ Questions */}
                  {questionForm.type === 'mcq_single' && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Answer Options</h3>
                      <div className="space-y-3">
                        {questionForm.options.map((option, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                            <div className="flex items-center">
                              <input
                                type={questionForm.type === 'mcq_single' ? 'radio' : 'checkbox'}
                                name="correctAnswer"
                                checked={option.isCorrect}
                                onChange={(e) => {
                                  const newOptions = [...questionForm.options];
                                  if (questionForm.type === 'mcq_single') {
                                    // For single choice, uncheck all others
                                    newOptions.forEach(opt => opt.isCorrect = false);
                                  }
                                  newOptions[index].isCorrect = e.target.checked;
                                  setQuestionForm(prev => ({ ...prev, options: newOptions }));
                                }}
                                className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300"
                              />
                            </div>
                            <input
                              type="text"
                              value={option.text}
                              maxLength={200}
                              onKeyPress={(e) => {
                                // Allow alphanumeric, spaces, and basic punctuation
                                const char = e.key;
                                if (!/^[a-zA-Z0-9\s\-_&().,;:!?'"]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab') {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                let value = e.target.value;
                                // Filter out invalid characters
                                value = value.replace(/[^a-zA-Z0-9\s\-_&().,;:!?'"]/g, '');
                                // Enforce max length
                                if (value.length > 200) {
                                  value = value.substring(0, 200);
                                }
                                const newOptions = [...questionForm.options];
                                newOptions[index].text = value;
                                setQuestionForm(prev => ({ ...prev, options: newOptions }));
                              }}
                              placeholder={`Option ${index + 1}`}
                              required
                              className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${(!option.text || option.text.trim().length === 0) && validationErrors.options ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                            />
                            <span className={`text-xs ml-2 whitespace-nowrap ${option.text.length > 180 ? 'text-red-600' :
                              option.text.length > 150 ? 'text-yellow-600' :
                                'text-gray-500'
                              }`}>
                              {option.text.length}/200
                            </span>
                            <PermissionGate permission="training" action="update">
                              <button
                                onClick={() => {
                                  if (questionForm.options.length > 2) {
                                    const newOptions = questionForm.options.filter((_, i) => i !== index);
                                    setQuestionForm(prev => ({ ...prev, options: newOptions }));
                                  }
                                }}
                                disabled={questionForm.options.length <= 2}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        ))}
                        {validationErrors.options && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.options}
                          </p>
                        )}

                        <PermissionGate permission="training" action="update">
                          <button
                            onClick={() => {
                              const newOption = {
                                text: '',
                                isCorrect: false
                              };
                              setQuestionForm(prev => ({
                                ...prev,
                                options: [...prev.options, newOption]
                              }));
                            }}
                            className="flex items-center px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Option
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  )}

                  {/* Correct Answer for Non-MCQ Questions */}
                  {questionForm.type === 'true_false' && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Correct Answer</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Correct Answer <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="correctAnswer"
                              value="true"
                              checked={questionForm.correctAnswer === 'true'}
                              onChange={(e) => {
                                setQuestionForm(prev => ({ ...prev, correctAnswer: e.target.value }));
                                if (validationErrors.correctAnswer) {
                                  setValidationErrors(prev => ({ ...prev, correctAnswer: null }));
                                }
                              }}
                              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">True</label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="correctAnswer"
                              value="false"
                              checked={questionForm.correctAnswer === 'false'}
                              onChange={(e) => {
                                setQuestionForm(prev => ({ ...prev, correctAnswer: e.target.value }));
                                if (validationErrors.correctAnswer) {
                                  setValidationErrors(prev => ({ ...prev, correctAnswer: null }));
                                }
                              }}
                              className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300"
                            />
                            <label className="ml-2 text-sm text-gray-700">False</label>
                          </div>
                          {validationErrors.correctAnswer && (
                            <p className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {validationErrors.correctAnswer}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Explanation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Explanation</label>
                    <textarea
                      value={questionForm.explanation}
                      maxLength={1000}
                      onKeyPress={(e) => {
                        // Allow alphanumeric, spaces, and basic punctuation for text areas
                        const char = e.key;
                        if (!/^[a-zA-Z0-9\s\-_&().,;:!?'"]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Filter out invalid characters
                        value = value.replace(/[^a-zA-Z0-9\s\-_&().,;:!?'"]/g, '');
                        // Enforce max length
                        if (value.length > 1000) {
                          value = value.substring(0, 1000);
                        }
                        setQuestionForm(prev => ({ ...prev, explanation: value }));
                      }}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent resize-none"
                      placeholder="Explain the correct answer..."
                    />
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs ${questionForm.explanation.length > 900 ? 'text-red-600' :
                        questionForm.explanation.length > 800 ? 'text-yellow-600' :
                          'text-gray-500'
                        }`}>
                        {questionForm.explanation.length}/1000
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
                <PermissionGate permission="training" action="read">
                  <button
                    onClick={() => setShowAddQuestion(false)}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </PermissionGate>
                <PermissionGate permission="training" action={editingQuestion ? "update" : "create"}>
                  <button
                    onClick={handleSaveQuestion}
                    className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                  >
                    <Save className="h-4 w-4 mr-2 inline" />
                    {editingQuestion ? 'Update Question' : 'Create Question'}
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Add/Edit Quiz Modal */}
      {
        showAddQuiz && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}
                </h2>
              </div>


              <div className="p-6">
                {/* Error Display */}
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Quiz Settings */}
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Quiz Title <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            name="title"
                            data-field="title"
                            value={quizForm.title}
                            maxLength={100}
                            required
                            onKeyPress={handleTextKeyPress}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Filter out invalid characters (alphanumeric, spaces, commas only)
                              value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                              // Enforce max length
                              if (value.length > 100) {
                                value = value.substring(0, 100);
                              }
                              setQuizForm(prev => ({ ...prev, title: value }));

                              if (touched.title) {
                                const error = validateField('title', value);
                                setValidationErrors(prev => ({ ...prev, title: error }));
                              }
                            }}
                            onBlur={handleBlur}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.title && validationErrors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                            placeholder="Enter quiz title"
                          />
                          <div className="flex justify-between items-start mt-1">
                            <div className="flex-1">
                              {touched.title && validationErrors.title && (
                                <p className="text-sm text-red-600 flex items-center">
                                  <AlertCircle className="h-4 w-4 mr-1" />
                                  {validationErrors.title}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 ml-2">
                              {quizForm.title?.length || 0}/100
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description <span className="text-red-500">*</span></label>
                          <textarea
                            name="description"
                            data-field="description"
                            value={quizForm.description}
                            maxLength={500}
                            required
                            onKeyPress={handleTextKeyPress}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Filter out invalid characters (alphanumeric, spaces, commas only)
                              value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                              // Enforce max length
                              if (value.length > 500) {
                                value = value.substring(0, 500);
                              }
                              setQuizForm(prev => ({ ...prev, description: value }));

                              if (touched.description) {
                                const error = validateField('description', value);
                                setValidationErrors(prev => ({ ...prev, description: error }));
                              }
                            }}
                            onBlur={handleBlur}
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent resize-none ${touched.description && validationErrors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                            placeholder="Enter quiz description"
                          />
                          <div className="flex justify-between items-start mt-1">
                            <div className="flex-1">
                              {touched.description && validationErrors.description && (
                                <p className="text-sm text-red-600 flex items-center">
                                  <AlertCircle className="h-4 w-4 mr-1" />
                                  {validationErrors.description}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 ml-2">
                              {quizForm.description?.length || 0}/500
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Assessment Settings */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Assessment Settings</h3>
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Pass Score (%) <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            name="passScore"
                            data-field="passScore"
                            required
                            value={quizForm.passScore === '' || quizForm.passScore === null || quizForm.passScore === undefined ? '' : String(quizForm.passScore).replace(/^0+/, '') || ''}
                            onKeyDown={handleNumberKeyDown}
                            onChange={(e) => {
                              let value = e.target.value;
                              value = value.replace(/[^0-9]/g, '');
                              if (value && Number(value) < 0) {
                                value = '0';
                              }
                              if (value && Number(value) > 100) {
                                value = '100';
                              }
                              const cleanValue = value.replace(/^0+/, '') || '';
                              setQuizForm(prev => ({
                                ...prev,
                                passScore: cleanValue === '' ? '' : parseInt(cleanValue) || ''
                              }));

                              if (touched.passScore) {
                                // Validate immediately against the new value
                                const error = validateField('passScore', cleanValue);
                                setValidationErrors(prev => ({ ...prev, passScore: error }));
                              }
                            }}
                            onBlur={handleBlur}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.passScore && validationErrors.passScore ? 'border-red-500 bg-red-50' : 'border-gray-300'
                              }`}
                            min="0"
                            max="100"
                            step="1"
                            placeholder="Enter pass score percentage"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Value: {quizForm.passScore || 0} (Max: 100%)
                          </p>
                          {touched.passScore && validationErrors.passScore && (
                            <p className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {validationErrors.passScore}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Show Solutions</label>
                          <select
                            value={quizForm.showSolutions}
                            onChange={(e) => setQuizForm(prev => ({ ...prev, showSolutions: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            {showSolutionsOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Question Selection */}
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Select Questions</h3>
                        <div className="flex items-center space-x-2">
                          <PermissionGate permission="training" action="read">
                            <button
                              onClick={handleSelectAllQuestions}
                              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                            >
                              Select All
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="training" action="read">
                            <button
                              onClick={handleClearSelection}
                              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              Clear All
                            </button>
                          </PermissionGate>
                        </div>
                      </div>

                      {/* Question Filters */}
                      <div className="space-y-3 mb-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search questions..."
                            value={questionSearchTerm}
                            onChange={(e) => {
                              // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                              const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                              setQuestionSearchTerm(value);
                            }}
                            onKeyPress={(e) => {
                              // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                              if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            maxLength={100}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                            autoComplete="off"
                            key="modal-search"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <select
                            value={questionSubjectFilter}
                            onChange={(e) => setQuestionSubjectFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm"
                          >
                            <option value="">All Courses</option>
                            {subjectOptions.length === 0 ? (
                              <option value="" disabled>
                                {coursesLoading ? 'Loading published courses...' : 'No published courses available'}
                              </option>
                            ) : (
                              subjectOptions.map(option => (
                                <option key={option.id} value={option.label}>
                                  {option.label}{option.isLegacy ? ' (Legacy)' : ''}
                                </option>
                              ))
                            )}
                          </select>

                          <select
                            value={questionDifficultyFilter}
                            onChange={(e) => setQuestionDifficultyFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm"
                          >
                            <option value="">All Difficulties</option>
                            {difficulties.map(diff => (
                              <option key={diff.value} value={diff.value}>{diff.label}</option>
                            ))}
                          </select>

                          <select
                            value={questionTypeFilter}
                            onChange={(e) => setQuestionTypeFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm"
                          >
                            <option value="">All Types</option>
                            {questionTypes.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Question List */}
                      <div className="max-h-96 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-4">
                        {getFilteredQuestionsForSelection().map(question => {
                          const isSelected = selectedQuestions.some(q => q && q.id === question.id);
                          const difficultyValue = getQuestionDifficultyValue(question);
                          const difficultyMeta = getDifficultyDisplay(difficultyValue);
                          const typeValue = getQuestionTypeValue(question);
                          const typeLabel =
                            questionTypes.find(t => t.value === typeValue)?.label || getQuestionTypeLabel(typeValue);
                          const pointsValue = getQuestionPointsValue(question);
                          const previewText = question.question ? `${question.question.substring(0, 100)}...` : '';
                          return (
                            <div
                              key={question.id}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                                ? 'border-agrosaf-green bg-green-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              onClick={() => handleSelectQuestion(question)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => { }}
                                      className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                                    />
                                    <h4 className="font-medium text-gray-900 text-sm">{question.title}</h4>
                                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                      {question.subject}
                                    </span>
                                  </div>
                                  {previewText && (
                                    <p className="text-xs text-gray-600 mb-2">{previewText}</p>
                                  )}
                                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                                    <span className={`px-2 py-1 rounded-full ${difficultyMeta.color}`}>
                                      {difficultyMeta.label}
                                    </span>
                                    <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                      {typeLabel}
                                    </span>
                                    <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                                      {pointsValue} pts
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Selected Questions Summary */}
                      {selectedQuestions.length > 0 && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <h4 className="font-medium text-green-900 mb-2">Selected Questions ({selectedQuestions.length})</h4>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {selectedQuestions.map(question => (
                              <div key={question.id} className="flex items-center justify-between text-sm">
                                <span className="text-green-800">{question.title}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveQuestion(question.id);
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 text-sm text-green-700">
                            Points: {selectedQuestions.reduce((sum, q) => sum + getQuestionPointsValue(q), 0)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {selectedQuestions.length} questions selected • {selectedQuestions.reduce((sum, q) => sum + getQuestionPointsValue(q), 0)} total points
                </div>
                <div className="flex items-center space-x-3">
                  <PermissionGate permission="training" action="read">
                    <button
                      onClick={() => setShowAddQuiz(false)}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </PermissionGate>
                  <PermissionGate permission="training" action={editingQuiz ? "update" : "create"}>
                    <button
                      onClick={handleSaveQuiz}
                      disabled={selectedQuestions.length === 0}
                      className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={selectedQuestions.length === 0 ? 'Please select at least one question to create a quiz' : 'Create quiz with selected questions'}
                    >
                      <Save className="h-4 w-4 mr-2 inline" />
                      {editingQuiz ? 'Update Quiz' : 'Create Quiz'}
                      {selectedQuestions.length > 0 && ` (${selectedQuestions.length} questions)`}
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        deleteConfirmation.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Deletion
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this {deleteConfirmation.type}? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <PermissionGate permission="training" action="read">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </PermissionGate>
                <PermissionGate permission="training" action="delete">
                  <button
                    onClick={handleConfirmDelete}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Deleting...' : 'Delete'}
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Deletion</h3>
              </div>
              <p className="text-gray-600 mb-2">
                Are you sure you want to delete <span className="font-semibold text-red-600">{selectedCount}</span> selected {activeTab === 'questions' ? 'question(s)' : 'quiz(zes)'}?
              </p>
              <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isBulkDeleting}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isBulkDeleting ? (
                    <>
                      <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedCount} Item{selectedCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Quiz Details Modal */}
      {
        viewingQuiz && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Quiz Details</h2>
                  <PermissionGate permission="training" action="read">
                    <button
                      onClick={handleCloseQuizDetails}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </PermissionGate>
                </div>

                <div className="space-y-6">
                  {/* Quiz Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Quiz Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <p className="text-gray-900 font-medium break-words capitalize">{viewingQuiz.title || 'Untitled quiz'}</p>
                      </div>
                      <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <p className="text-gray-900 whitespace-pre-wrap break-words">{viewingQuiz.description || 'No description provided'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pass Score</label>
                        <p className="text-gray-900 font-medium">{viewingQuiz.passScore ?? 0}%</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Show Solutions</label>
                        <p className="text-gray-900 capitalize font-medium">
                          {viewingQuiz.showSolutions?.replace('_', ' ') || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quiz Statistics */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Quiz Statistics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                        <div className="text-2xl font-bold text-gray-900">{viewingQuizQuestionCount}</div>
                        <div className="text-sm text-gray-500">Questions</div>
                      </div>
                      <div className="text-center p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                        <div className="text-2xl font-bold text-gray-900">{viewingQuizTotalPoints}</div>
                        <div className="text-sm text-gray-500">Points</div>
                      </div>
                      <div className="text-center p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                        <div className="text-2xl font-bold text-gray-900">{viewingQuiz.passScore ?? 0}%</div>
                        <div className="text-sm text-gray-500">Pass Score</div>
                      </div>
                    </div>
                  </div>

                  {/* Questions List */}
                  {viewingQuizQuestions.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Questions ({viewingQuizQuestionCount})</h3>
                      <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                        {viewingQuizQuestions.map((question, index) => {
                          const typeValue = getQuestionTypeValue(question);
                          const difficultyValue = getQuestionDifficultyValue(question);
                          const difficultyLabel = getDifficultyDisplay(difficultyValue).label;
                          const pointsValue = getQuestionPointsValue(question) || 0;
                          return (
                            <div key={question.id || index} className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                              <p className="font-semibold text-gray-900 break-words">
                                {question.title || question.question || `Question ${index + 1}`}
                              </p>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center space-x-1">
                                  <span className="text-gray-500">Type:</span>
                                  <span className="font-medium">{getQuestionTypeLabel(typeValue)}</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <span className="text-gray-500">Difficulty:</span>
                                  <span className="font-medium">{difficultyLabel}</span>
                                </span>
                                <span className="flex items-center space-x-1">
                                  <span className="text-gray-500">Points:</span>
                                  <span className="font-medium">{pointsValue}</span>
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                  <PermissionGate permission="training" action="read">
                    <button
                      onClick={handleCloseQuizDetails}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                  </PermissionGate>
                  <PermissionGate permission="training" action="update">
                    <button
                      onClick={() => {
                        handleCloseQuizDetails();
                        handleEditQuiz(viewingQuiz);
                      }}
                      className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                    >
                      Edit Quiz
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Question Details Modal */}
      {
        viewingQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Question Details</h2>
                  <PermissionGate permission="training" action="read">
                    <button
                      onClick={handleCloseQuestionDetails}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </PermissionGate>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <p className="text-gray-900 capitalize">{viewingQuestion.title || 'Untitled question'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                        <p className="text-gray-900 break-words">{viewingQuestion.subject || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                        <p className="text-gray-900">
                          {getDifficultyDisplay(getQuestionDifficultyValue(viewingQuestion)).label}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                        <p className="text-gray-900">{getQuestionTypeLabel(getQuestionTypeValue(viewingQuestion))}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                        <p className="text-gray-900">{getQuestionPointsValue(viewingQuestion)}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Question Text</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{viewingQuestion.question}</p>
                  </div>

                  {Array.isArray(viewingQuestion.options) && getQuestionTypeValue(viewingQuestion) === 'mcq_single' && viewingQuestion.options.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Answer Options</h3>
                      <div className="space-y-2">
                        {viewingQuestion.options.map((option, index) => (
                          <div
                            key={`${option.text}-${index}`}
                            className={`p-3 border rounded-lg ${option.isCorrect ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-gray-900">{option.text || `Option ${index + 1}`}</p>
                              {option.isCorrect && (
                                <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">Correct</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewingQuestion.type === 'true_false' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Correct Answer</h3>
                      <p className="text-gray-900 capitalize">{viewingQuestion.correctAnswer || 'Not set'}</p>
                    </div>
                  )}

                  {viewingQuestion.explanation && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Explanation</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{viewingQuestion.explanation}</p>
                    </div>
                  )}

                  {Array.isArray(viewingQuestion.tags) && viewingQuestion.tags.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {viewingQuestion.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                  <PermissionGate permission="training" action="read">
                    <button
                      onClick={handleCloseQuestionDetails}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                  </PermissionGate>
                  <PermissionGate permission="training" action="update">
                    <button
                      onClick={() => {
                        handleCloseQuestionDetails();
                        handleEditQuestion(viewingQuestion);
                      }}
                      className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
                    >
                      Edit Question
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title={`Export Training ${activeTab}`}
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: true,
        }}
      />
    </div >
  );
};

export default QuizQuestionBank;
